import React, { Dispatch, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    CircularProgress,
    DialogActions,
    DialogContent,
    Divider,
    FormLabel,
    Grid2 as Grid,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import DialogFrame from '../_parts/DialogFrame';
import CtaButton from '../_parts/CtaButton';
import DialogHeader from '../_parts/DialogHeader';
import { ContentLayout } from '../_parts/ContentLayout';
import {
    closeExcelUploadDialog,
    downloadExcelTemplate,
    uploadExcelTemplate,
    resetState,
} from '../../../../redux/project/sliceProjectExcelUpload';
import Message from '../_parts/Message';
import { mdiClose, mdiDownload, mdiInformation } from '@mdi/js';
import store, { IAppReduxStore } from '../../../../redux/store';
import { ToastSeverity } from '../../Toast/Toast';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { headingFontBold, vars } from '../../../../Theme';
import Icon from '@mdi/react';
import FileExplorerButton from '../_parts/FileExplorerButton';
import FileIcon from '../../Icons/FileIcon';

export default () => {
    const { replicaId } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'excelUploadDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const {
        isDialogOpen,
        loading,
        downloading,
        uploading,
        data: excelTemplates,
        error,
    } = useSelector((_store: IAppReduxStore) => _store.excelUpload);

    const [selected, setSelected] = useState<string>('');
    const [excelFile, setExcelFile] = useState<File>(null);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(t('errorLoading'), { severity: ToastSeverity.Error });
        }
    }, [error]);

    const onDownload = () => {
        dispatch(
            downloadExcelTemplate({
                replicaId,
                template: excelTemplates.find(x => x.key === selected),
            }),
        );
    };

    const onClose = () => {
        dispatch(closeExcelUploadDialog());
        dispatch(resetState());
    };

    const onConfirm = async () => {
        onClose();

        const upload = await store.dispatch(
            uploadExcelTemplate({ replicaId, excelFile, errorFileName: t('errorFileName') }),
        );
        if (upload.meta.requestStatus === 'fulfilled') {
            enqueueSnackbar(t('succeed'), { severity: ToastSeverity.Success });
        } else {
            enqueueSnackbar(t('failed', { errorFileName: t('errorFileName') }), {
                severity: ToastSeverity.Error,
            });
        }
    };

    useEffect(() => {
        if (excelTemplates && !selected) {
            setSelected(excelTemplates?.[0].key ?? '');
        }
    }, [excelTemplates]);

    return (
        isDialogOpen && (
            <DialogFrame open>
                <DialogHeader dialogTitle={t('dialogTitle')} onClose={onClose} />
                <DialogContent sx={{ pt: 0 }}>
                    <ContentLayout
                        loading={loading}
                        main={
                            <Stack spacing={2.5}>
                                <Message text={t('info')} icon={mdiInformation} />
                                <FormLabel
                                    sx={{ ...headingFontBold, fontSize: '16px', color: vars.black }}
                                >
                                    {t(`labelSelect`)}
                                </FormLabel>
                                <Grid container direction='row'>
                                    <Grid size='grow'>
                                        <Select
                                            disabled={loading || downloading}
                                            value={selected}
                                            onChange={(e: SelectChangeEvent) =>
                                                setSelected(e.target.value)
                                            }
                                            fullWidth
                                            size='small'
                                            sx={{
                                                '& .MuiInputBase-inputSizeSmall': {
                                                    py: '6.5px',
                                                    pl: '12px',
                                                },
                                            }}
                                        >
                                            {excelTemplates?.map(option => (
                                                <MenuItem key={option.key} value={option.key}>
                                                    {option.subject}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                    <Grid size='auto'>
                                        <IconButton
                                            disabled={loading || downloading}
                                            onClick={onDownload}
                                            title={t('labelDownload')}
                                        >
                                            {downloading ? (
                                                <CircularProgress size='24px' />
                                            ) : (
                                                <Icon path={mdiDownload} size={1} />
                                            )}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                                <FormLabel
                                    required
                                    sx={{ ...headingFontBold, fontSize: '16px', color: vars.black }}
                                >
                                    {t(`labelUpload`)}
                                </FormLabel>
                                {excelFile ? (
                                    <Stack direction='row' alignItems='center'>
                                        <FileIcon fileName={excelFile.name} />
                                        <Typography>{excelFile.name}</Typography>
                                        <IconButton onClick={() => setExcelFile(null)}>
                                            <Icon path={mdiClose} size={1} />
                                        </IconButton>
                                    </Stack>
                                ) : (
                                    <FileExplorerButton
                                        allowMultiple={false}
                                        label={t('labelUploadButton')}
                                        acceptFileTypes='.xlsx'
                                        onFileInput={files => setExcelFile(files?.[0])}
                                    />
                                )}
                            </Stack>
                        }
                    />
                </DialogContent>
                <Divider />
                <DialogActions>
                    <CtaButton text={t('ctaCancel')} variant='outlined' onClick={onClose} />
                    <CtaButton
                        disabled={!excelFile || uploading}
                        text={t('ctaConfirm')}
                        variant='contained'
                        onClick={onConfirm}
                    />
                </DialogActions>
            </DialogFrame>
        )
    );
};
